/**
 * Project Hub API functions — v4 client
 * canonical: S2 — lines 1303–1939
 *
 * Auth delegated to shared apiFetch helper in ./client.
 * All functions return typed data or throw on HTTP/network error.
 *
 * Endpoints used by PR-V4-PROJECTS-LIST:
 *   GET /api/projects                             — list (re-exported from dashboard)
 *   GET /api/projects/:id/hub/trades              — sub/trade list for modal Tab 1
 *   GET /api/projects/:id/hub/inbox               — inbox items for modal Tab 1 + Sub Detail
 *   GET /api/aria/lien-alerts?project_id=:id      — lien alerts for modal Tab 2
 *   GET /api/projects/:id/lien-docs               — filed lien docs for modal Tab 2
 *   GET /api/projects/:id/payapps                 — pay applications for modal Tab 3
 */

import { apiFetch, fetchWithAuthRetry, getToken, resolveApiUrl } from './client'
import type {
  Project,
  ProjectTrade,
  HubTradesResponse,
  HubInboxItem,
  HubInboxResponse,
  ReviewUploadBody,
  ReviewUploadResponse,
  LienAlert,
  LienAlertsResponse,
  LienDoc,
  PayApp,
  // PR-V4-NEW-PROJECT-WIZARD (canonical: S1 lines 722–1302)
  ContractorSettings,
  CreateProjectInput,
  SOVParseResult,
  SaveSOVInput,
  CreatePayAppInput,
  PayAppDetail,
  UpdatePayAppLinesInput,
  CreateNoticeDraftInput,
  NoticeDraft,
  // PR-V4-SUB-PORTAL
  ContractorVendor,
  // PR-V4-VENDOR-ACCEPT-INVITE
  ProjectVendorRow,
} from './types'

// ── Re-export fetchProjects so screens only need one import ───────────────

export { fetchProjects } from './dashboard'

// ── Project detail (derived from list) ────────────────────────────────────

/**
 * Fetch a single project by id by filtering the full list.
 * No dedicated GET /api/projects/:id endpoint exists — the list is the source.
 * canonical: S2 modal header — lines 1453–1490
 */
export async function fetchProject(id: number): Promise<Project | null> {
  const list = await apiFetch<Project[]>('GET', '/api/projects?include_test=1')
  return list.find(p => p.id === id) ?? null
}

// ── Modal Tab 1: Subs & Inbox ──────────────────────────────────────────────

/**
 * Trades (subcontractors) on a project.
 * GET /api/projects/:id/hub/trades → { data: ProjectTrade[] }
 * canonical: S2 lines 1563–1600 (pm-sub-row list)
 */
export async function fetchProjectTrades(projectId: number): Promise<ProjectTrade[]> {
  const res = await apiFetch<HubTradesResponse>('GET', `/api/projects/${projectId}/hub/trades`)
  return res.data
}

/**
 * Hub inbox items for a project. Optional status filter.
 * GET /api/projects/:id/hub/inbox?status=<all|pending|approved|rejected>
 * canonical: S2 lines 1601–1636 (pm-ibx-i items in Tab 1)
 *
 * Pass hubTradeId (project_trades.id) or projectVendorId for sub detail filtering.
 */
export async function fetchProjectInbox(
  projectId: number,
  opts: { status?: 'all' | 'pending' | 'approved' | 'rejected'; hubTradeId?: number; projectVendorId?: number } = {},
): Promise<HubInboxItem[]> {
  const params = new URLSearchParams()
  if (opts.status && opts.status !== 'all') params.set('status', opts.status)
  if (opts.hubTradeId != null) params.set('trade_id', String(opts.hubTradeId))
  if (opts.projectVendorId != null) params.set('project_vendor_id', String(opts.projectVendorId))
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await apiFetch<HubInboxResponse>(
    'GET',
    `/api/projects/${projectId}/hub/inbox${qs}`,
  )
  return res.data ?? []
}

/**
 * Review a hub upload (approve / reject / request changes).
 * PATCH /api/hub/:projectId/uploads/:uploadId/review
 */
export async function reviewHubUpload(
  projectId: number,
  uploadId: number,
  body: ReviewUploadBody,
): Promise<ReviewUploadResponse> {
  return apiFetch<ReviewUploadResponse>(
    'PATCH',
    `/api/hub/${projectId}/uploads/${uploadId}/review`,
    body,
  )
}

/** GET /api/projects/:id/sub-payments — per-sub invoice payment report */
export async function fetchSubPayments(projectId: number): Promise<{ data: import('./types').SubPaymentsRow[] }> {
  return apiFetch<{ data: import('./types').SubPaymentsRow[] }>('GET', `/api/projects/${projectId}/sub-payments`)
}

/** Download path for hub upload file (use with fetchHubUploadBlob for auth). */
export function hubUploadDownloadPath(projectId: number, uploadId: number): string {
  return resolveApiUrl(`/api/projects/${projectId}/hub/uploads/${uploadId}/download`)
}

/**
 * Fetch hub upload file as Blob (JWT auth — for PDF/image preview via object URL).
 */
export async function fetchHubUploadBlob(
  projectId: number,
  uploadId: number,
): Promise<Blob> {
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(hubUploadDownloadPath(projectId, uploadId), {
    headers,
    credentials: 'same-origin',
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? `HTTP ${res.status}`)
  }

  const contentType = res.headers.get('content-type') ?? ''
  if (contentType.includes('text/html')) {
    throw new Error('Preview unavailable — received HTML instead of a file (check API URL)')
  }
  if (contentType.includes('application/json')) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as { error?: string }).error ?? 'Preview unavailable')
  }

  const buffer = await res.arrayBuffer()
  const head = new Uint8Array(buffer.slice(0, 4))
  const isPdfMagic =
    head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46 // %PDF

  let blobType = contentType.split(';')[0].trim()
  if (isPdfMagic || blobType.includes('pdf')) {
    blobType = 'application/pdf'
  } else if (!blobType || blobType === 'application/octet-stream') {
    blobType = 'application/octet-stream'
  }

  return new Blob([buffer], { type: blobType })
}

// ── Modal Tab 2: Lien ─────────────────────────────────────────────────────

/**
 * Lien alerts scoped to a single project.
 * GET /api/aria/lien-alerts?project_id=:id
 * canonical: S2 lines 1667–1705 (Tab 2 lien panel — alert rows)
 */
export async function fetchProjectLienAlerts(projectId: number): Promise<LienAlert[]> {
  const res = await apiFetch<LienAlertsResponse>(
    'GET',
    `/api/aria/lien-alerts?project_id=${projectId}`,
  )
  return res.alerts
}

/**
 * Filed lien documents for a project (PDFs generated by ARIA).
 * GET /api/projects/:id/lien-docs → LienDoc[]
 * canonical: S2 lines 1667–1690 (Tab 2 — 20-day pre-lien "↓ PDF" button)
 */
export async function fetchProjectLienDocs(projectId: number): Promise<LienDoc[]> {
  return apiFetch<LienDoc[]>('GET', `/api/projects/${projectId}/lien-docs`)
}

// ── Modal Tab 3: Payments ─────────────────────────────────────────────────

/**
 * Pay applications for a project, ordered by app_number asc.
 * GET /api/projects/:id/payapps → PayApp[] (flat array)
 * canonical: S2 lines 1871–1902 (Tab 3 pay applications table)
 *
 * "Payments received" table (lines 1835–1860) is derived from this list:
 * filter to live PayApp rows with recorded amount_paid > 0 and display
 * amount_paid, last_payment_method, submitted_at as the payment date.
 */
export async function fetchProjectPayApps(projectId: number): Promise<PayApp[]> {
  return apiFetch<PayApp[]>('GET', `/api/projects/${projectId}/payapps`)
}

// ═══════════════ PR-V4-NEW-PROJECT-WIZARD (canonical: S1 lines 722–1302) ════════

/** GET /api/settings — contractor defaults for Step 1 auto-fill (canonical: S1 lines 756–800) */
export async function fetchContractorSettings(): Promise<ContractorSettings> {
  return apiFetch<ContractorSettings>('GET', '/api/settings')
}

/** POST /api/projects — create project record at wizard Step 1 submit (canonical: S1 lines 756–800) */
export class ProjectNumberConflictError extends Error {
  suggested_number: string | null
  constructor(message: string, suggested: string | null) {
    super(message)
    this.name = 'ProjectNumberConflictError'
    this.suggested_number = suggested
  }
}

export async function createProject(input: CreateProjectInput): Promise<{ id: number; [key: string]: unknown }> {
  const res = await fetchWithAuthRetry('/api/projects', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (res.status === 409) {
    const err = await res.json().catch(() => ({})) as { message?: string; suggested_number?: string }
    throw new ProjectNumberConflictError(
      err.message || 'Project number is already in use',
      err.suggested_number ?? null,
    )
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string; message?: string }
    throw new Error(err.message || err.error || `HTTP ${res.status}`)
  }
  return res.json() as Promise<{ id: number; [key: string]: unknown }>
}

/** PUT /api/projects/:id/full — update existing wizard project (reuse projectId on back-nav) */
export async function updateProject(
  projectId: number,
  input: CreateProjectInput,
): Promise<{ id: number; [key: string]: unknown }> {
  return apiFetch<{ id: number; [key: string]: unknown }>('PUT', `/api/projects/${projectId}/full`, input)
}

/** PATCH /api/projects/:id/test-flag — exclude project from billing sync */
export async function setProjectTestFlag(projectId: number, isTest: boolean): Promise<{ id: number; is_test_project: boolean }> {
  const res = await apiFetch<{ data: { id: number; is_test_project: boolean } }>(
    'PATCH', `/api/projects/${projectId}/test-flag`, { is_test_project: isTest },
  )
  return res.data
}

/** PATCH /api/projects/:id/completion — set/clear completed_at + recompute lien windows */
export async function setProjectCompletionDate(
  projectId: number,
  completedAt: string | null,
): Promise<{
  completed_at: string | null
  windows?: {
    mechanics_lien_date?: string | null
    retention_release_date?: string | null
  }
  lien_status?: string
}> {
  const res = await apiFetch<{
    data: {
      completed_at: string | null
      windows?: {
        mechanics_lien_date?: string | null
        retention_release_date?: string | null
      }
      lien_status?: string
    }
  }>('PATCH', `/api/projects/${projectId}/completion`, { completed_at: completedAt })
  return res.data
}

/** DELETE /api/projects/:id — soft delete (excluded from lists; blocked if paid pay apps exist) */
export async function deleteProject(projectId: number): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>('DELETE', `/api/projects/${projectId}`)
}

/** POST /api/projects/:id/archive — hide from hub/dashboard (reversible) */
export async function archiveProject(projectId: number): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>('POST', `/api/projects/${projectId}/archive`, {})
}

/** POST /api/projects/:id/unarchive — restore archived project */
export async function unarchiveProject(projectId: number): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>('POST', `/api/projects/${projectId}/unarchive`, {})
}

/** GET /api/projects/archived — archived projects list */
export async function fetchArchivedProjects(): Promise<Project[]> {
  return apiFetch<Project[]>('GET', '/api/projects/archived')
}

/**
 * POST /api/sov/parse — parse uploaded SOV file (canonical: S1 lines 802–834)
 * Uses raw fetch + FormData (NOT apiFetch) — multipart incompatible with JSON content-type wrapper.
 */
const SOV_PARSE_TIMEOUT_MS = 60_000

export async function parseSOVFile(file: File): Promise<SOVParseResult> {
  const allowed = [
    'text/csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ]
  if (!allowed.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls|pdf|docx?)$/i)) {
    throw new Error('Only CSV, Excel, PDF, and Word files are supported.')
  }
  const fd = new FormData()
  fd.append('file', file)
  // Multipart: no Content-Type header — fetch sets boundary. fetchWithAuthRetry
  // handles 401 → silent refresh → one retry (client.ts:88-112).
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), SOV_PARSE_TIMEOUT_MS)
  let res: Response
  try {
    res = await fetchWithAuthRetry('/api/sov/parse', {
      method: 'POST',
      body: fd,
      signal: controller.signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('SOV import timed out after 60 seconds. Try a smaller file or enter line items manually.')
    }
    throw err
  } finally {
    clearTimeout(timer)
  }
  if (res.status === 401) {
    throw new Error('Session expired — please sign in again.')
  }
  if (!res.ok) {
    const text = await res.text().catch(() => 'Upload failed')
    let message = text || `SOV parse failed: ${res.status}`
    try {
      const body = JSON.parse(text) as { error?: string }
      if (body.error) message = body.error
    } catch { /* plain-text error body */ }
    throw new Error(message)
  }
  return res.json() as Promise<SOVParseResult>
}

/** GET /api/projects/:id/sov/source-document — imported proposal file */
export function projectSourceDocumentUrl(projectId: number): string {
  return `/api/projects/${projectId}/sov/source-document`
}

/** POST /api/projects/:id/sov — save finalized SOV lines (canonical: S1 lines 802–915) */
export async function saveProjectSOV(projectId: number, input: SaveSOVInput): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>('POST', `/api/projects/${projectId}/sov`, input)
}

/** POST /api/projects/:id/sync-contract — sync contract value from SOV totals (canonical: S1 lines 836–915) */
export async function syncContractToSOV(projectId: number): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>('POST', `/api/projects/${projectId}/sync-contract`, {})
}

/** POST /api/projects/:id/payapps — create PA#1 draft; backend seeds lines at 20% (canonical: S1 lines 945–1000) */
export async function createPayAppDraft(projectId: number, input: CreatePayAppInput): Promise<{ id: number; [key: string]: unknown }> {
  return apiFetch<{ id: number; [key: string]: unknown }>('POST', `/api/projects/${projectId}/payapps`, input)
}

/** GET /api/payapps/:id — full pay app with lines + change orders (canonical: S1 lines 945–1000) */
export async function fetchPayAppDetail(payAppId: number): Promise<PayAppDetail> {
  return apiFetch<PayAppDetail>('GET', `/api/payapps/${payAppId}`)
}

export async function dismissPayAppRetentionNotice(payAppId: number): Promise<void> {
  await apiFetch('POST', `/api/payapps/${payAppId}/retention-notice-dismiss`)
}

/** PUT /api/payapps/:id/lines — bulk update G703 lines (canonical: S1 lines 945–1000) */
export async function updatePayAppLines(
  payAppId: number,
  input: UpdatePayAppLinesInput,
): Promise<{ ok: boolean; amount_due?: number; retention_held?: number }> {
  return apiFetch<{ ok: boolean; amount_due?: number; retention_held?: number }>(
    'PUT',
    `/api/payapps/${payAppId}/lines`,
    input,
  )
}

/** POST /api/projects/:id/pay-apps/:payAppId/record-payment — manual payment (check, wire, etc.) */
export async function recordManualPayment(
  projectId: number,
  payAppId: number,
  data: { amount: number; payment_method: string; check_number?: string; notes?: string },
): Promise<void> {
  await apiFetch('POST', `/api/projects/${projectId}/pay-apps/${payAppId}/record-payment`, data)
}

/** POST /api/projects/:id/retainage-release — prefills final retention-release pay app */
export async function createRetainageRelease(projectId: number): Promise<{
  pay_app_id: number
  app_number: number | null
  amount_due: number | null
  already_exists: boolean
}> {
  const res = await apiFetch<{
    data: {
      pay_app_id: number
      app_number: number | null
      amount_due: number | null
      already_exists: boolean
    }
  }>('POST', `/api/projects/${projectId}/retainage-release`, {})
  return res.data
}

export interface ChangeOrderTraceRow {
  id: number
  co_number: number
  description: string
  amount: number
  status: string
  pay_app_id: number
  app_number: number
  pay_app_status: string
  period_label: string | null
  billing_status: 'billed' | 'not_yet_billed' | 'void'
  amount_billed: number
  attribution_uncertain: boolean
}

/** GET /api/projects/:id/change-orders/trace — CO → pay app billing attribution */
export async function fetchChangeOrderTrace(projectId: number): Promise<ChangeOrderTraceRow[]> {
  const res = await apiFetch<{ data: { change_orders: ChangeOrderTraceRow[] } }>(
    'GET',
    `/api/projects/${projectId}/change-orders/trace`,
  )
  return res.data.change_orders
}

export type ChangeOrderLogEntry = {
  kind: 'change_order'
  id: number
  co_number: number
  title: string
  amount: number
  amount_display: string
  running_contract: number
  billing_status: 'billed' | 'partly_billed' | 'not_billed'
  billing_label: string
  pay_app_id?: number
  pay_app_number?: number
  created_at?: string
  attachments: Array<{ id: number; original_name?: string; filename?: string }>
}

export type ChangeOrderLogTree = {
  project: { id: number; name?: string; number?: string }
  original_contract: number
  entries: ChangeOrderLogEntry[]
  current_contract: number
  g702_line_c?: number | null
  generated_at: string
}

/** GET /api/projects/:id/change-order-log — read-only CO history tree */
export async function fetchChangeOrderLog(projectId: number): Promise<ChangeOrderLogTree> {
  const res = await apiFetch<{ data: ChangeOrderLogTree }>(
    'GET',
    `/api/projects/${projectId}/change-order-log`,
  )
  return res.data
}

/** GET /api/projects/:id/change-order-log.pdf — download printable CO log */
export async function downloadChangeOrderLogPdf(projectId: number): Promise<void> {
  const token = localStorage.getItem('ci_token')
  const res = await fetch(`/api/projects/${projectId}/change-order-log.pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) {
    throw new Error(`Failed to download change order log: HTTP ${res.status}`)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `change-order-log-${projectId}.pdf`
  a.click()
  URL.revokeObjectURL(url)
}

/** POST /api/projects/:id/notice-drafts — save pre-lien draft (canonical: S1 lines 1252–1279) */
export async function saveNoticeDraft(projectId: number, input: CreateNoticeDraftInput): Promise<NoticeDraft> {
  return apiFetch<NoticeDraft>('POST', `/api/projects/${projectId}/notice-drafts`, input)
}

/**
 * POST /api/aria/lien-alerts/:projectId — fetch CA pre-lien deadlines (canonical: S1 lines 1252–1279)
 * GUARD: Only call when jurisdiction === 'CA'. Non-CA shows deferred message client-side.
 */
export async function fetchLienAlertDeadlines(
  projectId: number,
  workStartDate: string,
  jurisdiction: string
): Promise<{ preliminary_notice_deadline: string; [key: string]: unknown }> {
  if (jurisdiction !== 'CA') {
    throw new Error('fetchLienAlertDeadlines: non-CA jurisdiction — call site must guard this.')
  }
  return apiFetch<{ preliminary_notice_deadline: string; [key: string]: unknown }>(
    'POST',
    `/api/aria/lien-alerts/${projectId}`,
    { work_start_date: workStartDate }
  )
}


// ── ARIA Lien Actions ─────────────────────────────────────────────────────

/**
 * Download the CA 20-Day Pre Lien Notice PDF for a project.
 * GET /api/aria/lien-alerts/:projectId/pdf
 * canonical: S2 lines 1667–1690 (Tab 2 — ↓ PDF button)
 *
 * Uses raw fetch rather than apiFetch — apiFetch calls res.json() which
 * cannot handle blob responses. Auth via Authorization: Bearer (L59 pattern:
 * same approach as parseSOVFile, which also needs raw fetch for non-JSON
 * response bodies).
 *
 * Throws on HTTP error or network failure so callers can surface the error
 * via toast or inline error state.
 */
function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null
  const star = /filename\*=UTF-8''([^;\s]+)/i.exec(header)
  if (star) {
    try { return decodeURIComponent(star[1]) } catch { /* fall through */ }
  }
  const plain = /filename="?([^";\n]+)"?/i.exec(header)
  return plain ? plain[1].trim() : null
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const anchor    = document.createElement('a')
  anchor.href     = objectUrl
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(objectUrl)
}

export async function downloadAriaLienNoticePdf(projectId: number): Promise<void> {
  const token = getToken()
  const url   = `${(import.meta.env.VITE_API_URL as string | undefined) ?? ''}/api/aria/lien-alerts/${projectId}/pdf`

  const res = await fetch(url, {
    method:  'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })

  if (!res.ok) {
    throw new Error(`Failed to download PDF: HTTP ${res.status}`)
  }

  const blob = await res.blob()
  triggerBlobDownload(blob, `20-day-pre-lien-notice-${projectId}.pdf`)
}

/**
 * Contractor files a document on a sub's behalf.
 * POST /api/projects/:id/subs/:projectVendorId/uploads  (project_vendors.id)
 * Uses XHR so callers can show upload progress on the file chip.
 */
export async function uploadContractorForSub(
  projectId: number,
  projectVendorId: number,
  file: File,
  docType: string,
  opts?: { insuranceExpires?: string | null },
  onProgress?: (pct: number) => void,
): Promise<HubInboxItem> {
  const token = getToken()
  const form = new FormData()
  form.append('file', file)
  form.append('doc_type', docType)
  if (opts?.insuranceExpires) {
    form.append('insurance_expires', opts.insuranceExpires)
  }

  const url = resolveApiUrl(`/api/projects/${projectId}/subs/${projectVendorId}/uploads`)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.upload.onprogress = (ev) => {
      if (!onProgress || !ev.lengthComputable) return
      onProgress(Math.round((ev.loaded / ev.total) * 100))
    }
    xhr.onload = () => {
      let json: { data?: HubInboxItem; error?: string } = {}
      try { json = JSON.parse(xhr.responseText || '{}') } catch { /* ignore */ }
      if (xhr.status >= 200 && xhr.status < 300 && json.data) {
        resolve(json.data)
        return
      }
      reject(new Error(json.error || `HTTP ${xhr.status}`))
    }
    xhr.onerror = () => reject(new Error('Network error'))
    xhr.send(form)
  })
}

/** Soft-remove a hub upload (sets deleted_at). Cross-tenant → 404. */
export async function softDeleteHubUpload(
  projectId: number,
  uploadId: number,
): Promise<void> {
  await apiFetch('DELETE', `/api/projects/${projectId}/hub/uploads/${uploadId}`)
}

/** PATCH doc_type / insurance_expires / amount. Status unchanged. Cross-tenant → 404. */
export async function updateHubUploadDocType(
  projectId: number,
  uploadId: number,
  body: {
    doc_type?: string
    insurance_expires?: string | null
    amount?: number | null
  },
): Promise<import('./types').HubInboxItem> {
  const res = await apiFetch<{ data: import('./types').HubInboxItem }>(
    'PATCH',
    `/api/projects/${projectId}/hub/uploads/${uploadId}`,
    body,
  )
  return res.data
}

export async function fetchHubUploadDeletePreview(
  projectId: number,
  uploadId: number,
): Promise<{ title: string; body: string; fileName?: string; subSentLine?: string | null }> {
  const res = await apiFetch<{
    data: { title: string; body: string; fileName?: string; subSentLine?: string | null }
  }>(
    'GET',
    `/api/projects/${projectId}/hub/uploads/${uploadId}/delete-preview`,
  )
  return res.data
}

/** Server duplicate check for contractor-for-sub uploads. */
export async function checkContractorSubUploadDuplicate(
  projectId: number,
  projectVendorId: number,
  file: File,
): Promise<boolean> {
  const q = new URLSearchParams({
    original_name: file.name,
    file_size: String(file.size),
  })
  const res = await apiFetch<{ data: { duplicate: boolean } }>(
    'GET',
    `/api/projects/${projectId}/subs/${projectVendorId}/uploads/duplicate?${q}`,
  )
  return Boolean(res.data?.duplicate)
}

/**
 * Download Project Vault zip for a project.
 * GET /api/projects/:id/archive.zip
 */
export async function downloadProjectVault(projectId: number): Promise<void> {
  const token = getToken()
  const base  = (import.meta.env.VITE_API_URL as string | undefined) ?? ''
  const url   = `${base}/api/projects/${projectId}/archive.zip`

  const res = await fetch(url, {
    method:  'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })

  if (!res.ok) {
    throw new Error(`Failed to download Project Vault: HTTP ${res.status}`)
  }

  const blob     = await res.blob()
  const fallback = `project-vault-${projectId}.zip`
  const filename = parseContentDispositionFilename(res.headers.get('Content-Disposition'))
    || fallback
  triggerBlobDownload(blob, filename)
}


// ═══════════════ PR-V4-SUB-PORTAL ════════════════════════════════════════

/**
 * Contractor vendor address book.
 * GET /api/contractor/vendors → ContractorVendor[]
 * canonical: PR-V4-SUB-PORTAL — InviteSubModal vendor list
 */
export async function fetchContractorVendors(): Promise<ContractorVendor[]> {
  const res = await apiFetch<{ data: ContractorVendor[] } | ContractorVendor[]>(
    'GET',
    '/api/contractor/vendors',
  )
  // Handle both wrapped { data: [...] } and flat array shapes
  return Array.isArray(res) ? res : (res as { data: ContractorVendor[] }).data
}

/**
 * Send magic-link invite emails to selected vendors for a project.
 * POST /api/contractor/projects/:id/invite
 * Body: { vendor_ids: number[] }
 * canonical: PR-V4-SUB-PORTAL — InviteSubModal send action
 */
export async function inviteVendorsToProject(
  projectId: number,
  vendorIds: number[],
  opts?: { confirmSelfInvite?: boolean },
): Promise<void> {
  await apiFetch<{ data: { invited: unknown[] } }>(
    'POST',
    `/api/contractor/projects/${projectId}/invite`,
    {
      vendor_ids: vendorIds,
      ...(opts?.confirmSelfInvite ? { confirm_self_invite: true } : {}),
    },
  )
}

/**
 * Create or upsert a vendor in the contractor address book.
 * POST /api/contractor/vendors
 * Body: { company_name, email, contact_name?, trade?, phone?, notes? }
 * canonical: PR-V4-SUB-PORTAL — InviteSubModal email invite path
 */
export async function createContractorVendor(
  input: { company_name: string; email: string; contact_name?: string; trade?: string },
): Promise<ContractorVendor> {
  const res = await apiFetch<{ data: ContractorVendor }>(
    'POST',
    '/api/contractor/vendors',
    input,
  )
  return res.data
}

// ── /api/contractor/projects/:id/vendors ─────────────────────────────────────
/**
 * List all vendors invited/accepted on a project (v4 invite flow).
 * GET /api/contractor/projects/:id/vendors
 *
 * Maps the contractor-vendors response shape to ProjectTrade so SubsTab
 * can render it without a separate type.  Root-cause fix for Bug 1 in
 * PR-V4-VENDOR-ACCEPT-INVITE: SubsTab was calling fetchProjectTrades()
 * (hits project_trades, empty for v4 flow) — this replaces it.
 * canonical: PR-V4-VENDOR-ACCEPT-INVITE
 */
export async function fetchProjectVendors(projectId: number): Promise<ProjectTrade[]> {
  const res = await apiFetch<{ data: ProjectVendorRow[] }>(
    'GET',
    `/api/contractor/projects/${projectId}/vendors`,
  )
  return (res.data ?? []).map((v): ProjectTrade => ({
    id:               v.project_vendor_id,
    project_trade_id: v.project_trade_id ?? null,
    project_id:       projectId,
    name:             v.trade ?? v.company_name,
    company_name:     v.company_name,
    contact_name:     v.contact_name,
    contact_email:    v.email,
    magic_link_token: v.invite_token,
    email_alias:      null,
    status:           v.status as ProjectTrade['status'],
    invite_sent_at:   v.invited_at,
    created_at:       v.invited_at,
    vendor_user_id:   v.vendor_user_id ?? null,
    vendor_id:        v.vendor_id,
  }))
}
